<template>
  <div class="pa-plan">
    <div class="pa-plan__mid">
      <div class="pa-plan__tags">
        <!-- page type: admin dropdown, otherwise a chip -->
        <span v-if="isAdmin" ref="pageTypeMenu" class="pa-menuwrap">
          <button
            type="button"
            class="pa-tagdd pa-tagdd--admin"
            aria-haspopup="menu"
            :aria-expanded="openMenu === 'pageType' ? 'true' : 'false'"
            @click="toggleMenu('pageType')"
          >
            {{ pageTypeLabel || 'Page type' }}
            <i class="material-icons" aria-hidden="true">expand_more</i>
          </button>
          <div v-if="openMenu === 'pageType'" class="pa-menu" role="menu">
            <button type="button" class="pa-menu__item" role="menuitem" @click="pickPageType('new')">New page</button>
            <button type="button" class="pa-menu__item" role="menuitem" @click="pickPageType('existing')">Existing page</button>
          </div>
        </span>
        <span v-else-if="pageTypeLabel" class="pa-tagdd">{{ pageTypeLabel }}</span>

        <span v-if="state === 'completed'" class="pa-tagdd pa-tagdd--green">Completed</span>
        <span v-else-if="state === 'in_progress'" class="pa-tagdd pa-tagdd--blue">In progress</span>

        <span v-if="cycleLabel" class="pa-tag pa-tag--purple">
          {{ cycleLabel }}<span v-if="isCurrentCycle && !locked" class="pa-tag__hint"> &middot; current</span>
        </span>
        <span v-if="locked" class="pa-plan__lock" title="This month is billed: the plan is locked">
          <i class="material-icons" aria-hidden="true">lock</i>
          Billed
        </span>
      </div>

      <h4 class="pa-plan__name">
        <!-- admin rename: click the name or "Rename" in the menu; Enter or blur saves, Escape cancels -->
        <input
          v-if="editingName"
          ref="nameInput"
          v-model="editedName"
          type="text"
          class="pa-in pa-plan__name-input"
          aria-label="Content plan name"
          maxlength="100"
          @keydown.enter.prevent="saveName"
          @keydown.esc.prevent.stop="cancelRename"
          @blur="saveName"
        />
        <span
          v-else-if="isAdmin"
          class="pa-plan__name-text pa-plan__name-text--editable"
          role="button"
          tabindex="0"
          title="Click to rename"
          @click="startRename"
          @keydown.enter.prevent="startRename"
        >{{ displayName }}</span>
        <span v-else class="pa-plan__name-text">{{ displayName }}</span>
        <!-- design PlanMenu: a plain menu anchored under the dots, closed by a click elsewhere or Escape -->
        <span v-if="isAdmin && !editingName" ref="actionsMenu" class="pa-menuwrap">
          <button
            type="button"
            class="pa-dots"
            aria-label="More actions"
            aria-haspopup="menu"
            :aria-expanded="openMenu === 'actions' ? 'true' : 'false'"
            @click="toggleMenu('actions')"
          >
            <i class="material-icons" aria-hidden="true">more_horiz</i>
          </button>
          <div v-if="openMenu === 'actions'" class="pa-menu" role="menu">
            <button type="button" class="pa-menu__item" role="menuitem" @click="startRename">Rename</button>
            <button type="button" class="pa-menu__item" role="menuitem" @click="openInNewTab">Open in new tab</button>
            <button type="button" class="pa-menu__item" role="menuitem" @click="askDelete">Delete</button>
          </div>
        </span>
      </h4>

      <div class="pa-plan__kw">
        <b>Main page:</b> {{ silo.topLevelKeywordLabel || 'Not set' }}
        &bull; {{ supportingCount }} supporting {{ supportingCount === 1 ? 'article' : 'articles' }}
      </div>

      <div v-if="showNoSelectionHint" class="pa-plan__warn">
        <i class="material-icons" aria-hidden="true">warning</i>
        No keywords selected: this plan is skipped when {{ cycleLabel }} is billed.
      </div>
      <!-- the selection against the package the client chose: too many / missing pages (admin) -->
      <div
        v-for="flag in capFlags"
        :key="flag.key"
        class="pa-plan__warn"
        :class="{ 'pa-plan__warn--danger': flag.tone === 'danger' }"
      >
        <i class="material-icons" aria-hidden="true">{{ flag.tone === 'danger' ? 'error' : 'warning' }}</i>
        {{ flag.text }}
      </div>

      <!-- a plan ordered outside a package (White Glove era) can never be scheduled: no month select -->
      <div v-if="isAdmin && !orderedOutsidePackage" class="pa-plan__schedule">
        <span class="pa-plan__schedule-label">Schedule (admin):</span>
        <select
          class="pa-sel pa-plan__schedule-select"
          :value="silo.paScheduledCycle || ''"
          :disabled="locked || saving"
          @change="onScheduleChange($event.target.value)"
        >
          <option value="">Unscheduled</option>
          <option v-for="n in monthOptions" :key="n" :value="n">
            {{ cycleLabelFor(n) }}{{ n === currentCycle ? ' (current)' : '' }}
          </option>
        </select>
        <span v-if="locked" class="pa-plan__schedule-note">billed</span>
      </div>
    </div>

    <button type="button" class="pa-btn pa-btn--orange-out pa-btn--pill" @click="view">View</button>

    <div v-if="showDeleteConfirm" class="modal is-active">
      <div class="modal-background" @click="showDeleteConfirm = false"></div>
      <div class="modal-content">
        <div class="box modal-container">
          <div class="pa-modal">
            <div class="pa-modal__title">Delete this content plan?</div>
            <div class="pa-modal__body">"{{ silo.name }}" and its keywords will be removed from the project. This cannot be undone.</div>
            <div class="pa-modal__actions">
              <button type="button" class="pa-btn pa-btn--out" @click="showDeleteConfirm = false">Cancel</button>
              <button type="button" class="pa-btn pa-btn--orange" :disabled="saving" @click="deleteSilo">Delete</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  popAuthorityCycleLabel,
  popAuthorityCycleMonthName,
  popAuthorityMonthOptions,
  popAuthorityPlanCapFlags,
  popAuthorityPlanHasSelection,
  popAuthorityPlanOrderedOutsidePackage,
  popAuthorityPlanState,
  showPopAuthorityToast,
} from '@/utils/popAuthority'

/**
 * POP Authority content plan card (design pa-tabs.jsx PlansTab). Page type / state / month tags,
 * "Main page + N supporting", the admin month scheduler, the admin "..." menu and the admin rename.
 */
export default {
  name: 'PopAuthorityContentPlanCard',
  inject: {
    refreshWhiteGloveOrders: { default: null },
  },
  props: {
    silo: { type: Object, required: true },
    /** Effective package {key, name, silos_per_month, supporting_per_silo, provisional} */
    packageInfo: { type: Object, required: true },
    currentCycle: { type: Number, default: 1 },
    /** Subscription anchor: turns "Month 2" into "October 2026" */
    cycleAnchorAt: { type: String, default: null },
    refreshData: { type: Function, required: true },
  },
  data () {
    return {
      saving: false,
      showDeleteConfirm: false,
      /** Open card menu: 'pageType' | 'actions' | null */
      openMenu: null,
      editingName: false,
      editedName: '',
      /** The saved name, shown until the refreshed plan list carries it (no flash of the old name) */
      pendingName: '',
    }
  },
  computed: {
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isAdmin () {
      return this.isMissionControlSession && Boolean(this.$store.state.isWhiteGloveAdminMode)
    },
    displayName () {
      return this.pendingName || this.silo.name
    },
    pageTypeLabel () {
      if (this.silo.pageType === 'new') return 'New page'
      if (this.silo.pageType === 'existing') return 'Existing page'
      return ''
    },
    state () {
      return popAuthorityPlanState(this.silo)
    },
    locked () {
      return Boolean(this.silo.paScheduleLockedAt)
    },
    orderedOutsidePackage () {
      return popAuthorityPlanOrderedOutsidePackage(this.silo)
    },
    cycleLabel () {
      const n = Number(this.silo.paScheduledCycle)
      return Number.isFinite(n) && n >= 1 ? popAuthorityCycleMonthName(n, this.cycleAnchorAt) : ''
    },
    isCurrentCycle () {
      const n = Number(this.silo.paScheduledCycle)
      return Number.isFinite(n) && n === Number(this.currentCycle)
    },
    hasSelection () {
      return popAuthorityPlanHasSelection(this.silo)
    },
    /** A scheduled plan with no keywords is skipped when its month is billed: tell the admin. */
    showNoSelectionHint () {
      return this.isAdmin && this.state === 'scheduled' && !this.hasSelection && !this.capFlags.length
    },
    /** Too many / missing pages against the chosen package (nothing while no package is chosen). */
    capFlags () {
      if (!this.isAdmin || this.orderedOutsidePackage) return []
      return popAuthorityPlanCapFlags(this.silo, this.packageInfo)
    },
    supportingCount () {
      const n = Number(this.silo.supportingArticlesCount)
      return Number.isFinite(n) ? n : 0
    },
    monthOptions () {
      return popAuthorityMonthOptions(this.currentCycle, this.silo.paScheduledCycle)
    },
  },
  mounted () {
    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('keydown', this.onDocumentKeydown)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('keydown', this.onDocumentKeydown)
  },
  methods: {
    toggleMenu (name) {
      this.openMenu = this.openMenu === name ? null : name
    },
    closeMenu () {
      this.openMenu = null
    },
    /** A click anywhere outside the open menu (another card's menu included) closes it. */
    onDocumentClick (event) {
      if (!this.openMenu) return
      const wrap = this.$refs[this.openMenu === 'actions' ? 'actionsMenu' : 'pageTypeMenu']
      if (wrap && wrap.contains(event.target)) return
      this.closeMenu()
    },
    onDocumentKeydown (event) {
      if (event.key === 'Escape') this.closeMenu()
    },
    pickPageType (pageType) {
      this.closeMenu()
      return this.updateSilo({ pageType })
    },
    startRename () {
      this.closeMenu()
      if (this.saving) return
      this.editedName = this.silo.name || ''
      this.editingName = true
      this.$nextTick(() => {
        const input = this.$refs.nameInput
        if (input) {
          input.focus()
          input.select()
        }
      })
    },
    cancelRename () {
      this.editingName = false
    },
    /** Enter and the blur that follows both land here: the first call ends the edit. */
    saveName () {
      if (!this.editingName) return
      this.editingName = false
      const name = (this.editedName || '').trim()
      if (!name || name === this.silo.name) return
      this.pendingName = name
      return this.updateSilo({ name }).finally(() => {
        this.pendingName = ''
      })
    },
    askDelete () {
      this.closeMenu()
      this.showDeleteConfirm = true
    },
    cycleLabelFor (n) {
      return popAuthorityCycleLabel(n, this.cycleAnchorAt)
    },
    detailLocation () {
      return {
        name: 'ContentPlanDetailWhiteGlove',
        params: { siteId: String(this.silo.siteId), siloId: String(this.silo.id) },
      }
    },
    view () {
      this.$router.push(this.detailLocation())
    },
    openInNewTab () {
      this.closeMenu()
      window.open(this.$router.resolve(this.detailLocation()).href, '_blank')
    },
    onScheduleChange (value) {
      const cycle = value === '' ? null : parseInt(value, 10)
      return this.updateSilo({ paScheduledCycle: Number.isFinite(cycle) ? cycle : null })
    },
    updateSilo (payload) {
      if (this.saving) return Promise.resolve()
      this.saving = true
      return this.$store
        .dispatch('updateSilo', { siloId: this.silo.id, body: payload })
        .then((response) => {
          const data = response && response.data
          if (data && data.status === 'SUCCESS') {
            if ('paScheduledCycle' in payload) {
              // design PlansTab toast: “name” scheduled for <month> / “name” unscheduled
              showPopAuthorityToast(this, payload.paScheduledCycle
                ? `\u201c${this.silo.name}\u201d scheduled for ${popAuthorityCycleLabel(payload.paScheduledCycle, this.cycleAnchorAt)}`
                : `\u201c${this.silo.name}\u201d unscheduled`)
            }
            if ('name' in payload) {
              showPopAuthorityToast(this, `Renamed to \u201c${payload.name}\u201d`)
            }
            return this.refreshData({ silent: true })
          }
          this.$notify({ group: 'info', type: 'error', text: (data && data.msg) || 'Could not update the content plan' })
          return this.refreshData({ silent: true })
        })
        .catch((error) => {
          const msg = error?.response?.data?.msg || 'Could not update the content plan'
          this.$notify({ group: 'info', type: 'error', text: msg })
          return this.refreshData({ silent: true })
        })
        .finally(() => {
          this.saving = false
        })
    },
    deleteSilo () {
      if (this.saving) return
      this.saving = true
      this.$store.commit('showLoading')
      return this.$store
        .dispatch('deleteSilo', this.silo.id)
        .then(() => {
          this.showDeleteConfirm = false
          const tasks = [this.refreshData({ silent: true })]
          if (typeof this.refreshWhiteGloveOrders === 'function') tasks.push(this.refreshWhiteGloveOrders())
          return Promise.all(tasks)
        })
        .catch(() => {
          this.$notify({ group: 'info', type: 'error', text: 'Could not delete the content plan' })
        })
        .finally(() => {
          this.$store.commit('hideLoading')
          this.saving = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup/setup.scss';

.pa-plan {
  border: 1px solid $pa-border-soft;
  border-radius: 10px;
  background: #fff;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  color: $pa-teal;

  &__mid {
    flex: 1;
    min-width: 0;
  }

  &__tags {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 8px;
  }

  &__lock {
    color: $pa-faint;
    display: inline-flex;

    .material-icons {
      font-size: 16px;
    }
  }

  &__name {
    margin: 10px 0 6px;
    font-size: 1.15rem;
    font-weight: 800;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    /* only the name truncates: a rule on every span would clip the menu anchored in this row */
    &-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;

      &--editable {
        cursor: text;
        border-radius: 4px;

        &:hover,
        &:focus-visible {
          text-decoration: underline dashed $pa-faint;
          text-underline-offset: 4px;
          outline: none;
        }
      }
    }

    /* sized to the heading so the row keeps its height while renaming */
    &-input {
      flex: 1;
      min-width: 0;
      max-width: 520px;
      padding: 4px 10px;
      font-size: 1.05rem;
      font-weight: 700;
    }
  }

  &__kw {
    font-size: 0.88rem;
    color: $pa-muted;

    b {
      color: $pa-teal;
    }
  }

  /* what the campaign manager has to fix before the month is produced: orange advice, red when it blocks */
  &__warn {
    margin-top: 6px;
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 0.82rem;
    font-weight: 600;
    color: $pa-accent;

    .material-icons {
      font-size: 16px;
      line-height: 1.3;
    }

    &--danger {
      color: $pa-danger;
    }
  }

  &__schedule {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.82rem;
    font-weight: 700;
    flex-wrap: wrap;
  }

  &__schedule-label {
    color: $pa-muted;
  }

  /* design PlansTab: a plain .pa-sel (white, #ccc border, teal text, caret), sized 4px 8px / .82rem;
     only the "Schedule (admin):" label marks it as an admin control */
  &__schedule-select {
    width: auto;
    padding: 4px 28px 4px 8px;
    font-size: 0.82rem;
    font-weight: 400;
    background-position: right 8px center;
  }

  &__schedule-note {
    color: $pa-faint;
    font-weight: 600;
  }
}

.pa-tagdd {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid $pa-border-soft;
  background: var(--v-pink1Color-base, #f7f5fb);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.82rem;
  font-weight: 700;
  color: $pa-teal;
  font-family: inherit;
  cursor: default;

  .material-icons {
    font-size: 16px;
    color: $pa-faint;
  }

  &--admin {
    cursor: pointer;
  }

  &--green {
    color: $pa-success;
  }

  &--blue {
    color: $pa-info;
  }
}

.pa-tag {
  display: inline-block;
  border-radius: 6px;
  padding: 3px 9px;
  font-size: 0.75rem;
  font-weight: 700;

  &--purple {
    background: #efe9fa;
    color: #7a5af8;
  }
}

.pa-dots {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  color: $pa-muted;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;

  &:hover {
    background: $pa-box-bg;
  }

  .material-icons {
    font-size: 18px;
  }
}

/* design pa.css .pa-menu: white, soft border, 10px radius, menu shadow, 170px min, anchored under its button */
.pa-menuwrap {
  position: relative;
  display: inline-flex;
  flex: none;
}

.pa-menu {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  background: #fff;
  border: 1px solid $pa-border-soft;
  border-radius: 10px;
  box-shadow: 0 4px 18px rgba(1, 71, 81, 0.14);
  z-index: 30;
  min-width: 170px;
  padding: 6px 0;
  text-align: left;

  &__item {
    display: block;
    width: 100%;
    border: 0;
    background: transparent;
    padding: 10px 16px;
    font-family: inherit;
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.45;
    color: $pa-teal;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      background: $pa-box-bg;
      outline: none;
    }
  }
}
</style>
